import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiPlus, FiUsers, FiUserCheck, FiTrash2 } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import SearchInput from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import StatusPill from '../../components/ui/StatusPill';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import EmptyState from '../../components/ui/EmptyState';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { listUsers, deactivateUser } from '../../api/admin';
import { extractErrorMessage } from '../../api/client';
import { joinName } from '../../utils/format';
import CreateAdminModal from './CreateAdminModal';
import EditAdminModal from './EditAdminModal';
import DepartmentsModal from './DepartmentsModal';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'USER', label: 'USER' },
  { value: 'ADMIN', label: 'ADMIN' },
  { value: 'SUPERADMIN', label: 'SUPERADMIN' },
  { value: 'TESTER', label: 'TESTER' },
];

function RoleBadge({ role }) {
  const tone =
    role === 'SUPERADMIN' ? 'danger' : role === 'TESTER' ? 'accent' : role === 'ADMIN' ? 'primary' : 'neutral';
  return <Badge tone={tone}>{role || '—'}</Badge>;
}

export default function AdminUsersListPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deptTarget, setDeptTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
  });

  const deactivate = useMutation({
    mutationFn: (id) => deactivateUser(id),
    onSuccess: () => {
      toast.success('User deactivated');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setDeactivateTarget(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const filtered = useMemo(() => {
    const list = data || [];
    const term = search.trim().toLowerCase();
    return list.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (activeOnly && !u.isActive) return false;
      if (!term) return true;
      const name = joinName(u.firstName, u.middleName, u.lastName);
      return (
        (u.username && u.username.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.employeeId && u.employeeId.toLowerCase().includes(term)) ||
        (name && name.toLowerCase().includes(term)) ||
        (u.department && u.department.toLowerCase().includes(term))
      );
    });
  }, [data, search, roleFilter, activeOnly]);

  const columns = [
    {
      key: 'employeeId',
      header: 'Employee ID',
      render: (r) => <span className="font-mono text-xs">{r.employeeId || '—'}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <div>
          <div className="font-medium text-ink">
            {joinName(r.firstName, r.middleName, r.lastName) || r.username}
          </div>
          <div className="text-xs text-ink-muted">{r.email}</div>
        </div>
      ),
    },
    { key: 'department', header: 'Department' },
    { key: 'jobTitle', header: 'Job Title' },
    {
      key: 'role',
      header: 'Role',
      render: (r) => <RoleBadge role={r.role} />,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (r) => <StatusPill value={r.isActive ? 'ACTIVE' : 'INACTIVE'} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setEditTarget(r);
            }}
            title="Edit User"
          >
            Edit
          </Button>

          <div className={r.role === 'ADMIN' ? '' : 'invisible pointer-events-none'}>
            <Button
              size="sm"
              variant="ghost"
              icon={FiUserCheck}
              onClick={(e) => {
                e.stopPropagation();
                setDeptTarget(r);
              }}
              title="Manage dept access"
            >
              Departments
            </Button>
          </div>

          <div className={r.isActive && r.role !== 'SUPERADMIN' ? '' : 'invisible pointer-events-none'}>
            <Button
              size="sm"
              variant="ghost"
              icon={FiTrash2}
              onClick={(e) => {
                e.stopPropagation();
                setDeactivateTarget(r);
              }}
              title="Deactivate"
            >
              Deactivate
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Admin Users" breadcrumb="Administration / Users">
      <PageHeader
        title="User management"
        description="Manage admin users, assign department access, and control activation."
        actions={
          <Button icon={FiPlus} onClick={() => setShowCreate(true)}>
            Create user
          </Button>
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorBanner error={error} title="Failed to load users" />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search name, email, employee ID..."
          className="w-full sm:max-w-sm"
        />
        <Select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="sm:w-44"
        >
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="rounded border-slate-300 text-primary focus:ring-primary/30"
          />
          Active only
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        keyField="id"
        empty={
          <EmptyState
            icon={FiUsers}
            title="No users yet"
            description="Create a user to start managing access."
            action={
              <Button icon={FiPlus} onClick={() => setShowCreate(true)}>
                Create user
              </Button>
            }
          />
        }
      />

      {showCreate && (
        <CreateAdminModal open={showCreate} onClose={() => setShowCreate(false)} />
      )}

      {editTarget && (
        <EditAdminModal
          open={!!editTarget}
          user={editTarget}
          users={data || []}
          onClose={() => setEditTarget(null)}
        />
      )}

      {deptTarget && (
        <DepartmentsModal
          open={!!deptTarget}
          user={deptTarget}
          onClose={() => setDeptTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate user?"
        message={
          deactivateTarget
            ? `This will deactivate "${deactivateTarget.username}". They will lose access until reactivated.`
            : ''
        }
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivate.isPending}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => deactivate.mutate(deactivateTarget.id)}
      />
    </AppLayout>
  );
}
